import { Nullable } from "util/extendedTypes";

export type Predicate<P> = (p: P) => boolean;
export type ValidationFunc<T> = (v: T) => string[];

export const FIELD_REQUIRED_MSG = "This field is required";

export const nullableValidation = <T>(
  func: ValidationFunc<T>,
  message: string = FIELD_REQUIRED_MSG
): ValidationFunc<Nullable<T>> => {
  return (v: Nullable<T>) =>
    v === null || v === undefined ? [message] : func(v);
};

export const conjuctPredicates =
  <P>(p1: Predicate<P>, p2: Predicate<P>): Predicate<P> =>
  (p: P) =>
    p1(p) && p2(p);

export const disjunctPredicates =
  <P>(p1: Predicate<P>, p2: Predicate<P>): Predicate<P> =>
  (p: P) =>
    p1(p) || p2(p);

export const contravariantCompose = <P>(...ps: Predicate<P>[]): Predicate<P> =>
  ps.reduce((acc, fn) => conjuctPredicates(acc, fn));

export const digitsOnly = (s: string) => /^\d+$/g.test(s);

export const alphabetsOnly = (s: string) => /^[A-Za-z]+$/g.test(s);

export const validationText =
  <P>(validationPredicate: Predicate<P>, message: string): ValidationFunc<P> =>
  (p: P) =>
    validationPredicate(p) ? [""] : [message];

type ValidationMap<T extends {}> = { [k in keyof T]: ValidationFunc<T[k]> };

export const composeValidation = <T>(
  ...fs: ValidationFunc<T>[]
): ValidationFunc<T> => {
  return (t: T): string[] =>
    fs.reduce((acc, f) => [...acc, ...f(t)], [] as string[]);
};

export const validateEither = <T>(
  f1: ValidationFunc<T>,
  f2: ValidationFunc<T>
): ValidationFunc<T> => {
  return (t: T) => {
    const res1 = f1(t);
    const res2 = f2(t);
    return res1.length < res2.length ? res1 : res2;
  };
};

export const validateAlternatives = <T>(
  ...funcs: ValidationFunc<T>[]
): ValidationFunc<T> => {
  return funcs.reduce(validateEither);
};

export const validateUntilFirstFail = <T>(
  ...funcs: ValidationFunc<T>[]
): ValidationFunc<T> => {
  return (t: T) => {
    for (let fn of funcs) {
      const res = fn(t);
      if (!!res.length) {
        return res;
      }
    }
    return [];
  };
};

export const validateObject = <T extends {}>(
  vObj: Partial<ValidationMap<T>>
): ValidationFunc<T> => {
  return (obj: T): string[] => {
    const vKeys = Object.keys(vObj) as (keyof T)[];
    return vKeys.reduce((acc, key) => {
      const validation = vObj[key];
      const messages = validation ? validation(obj[key]) : [];
      return [...acc, ...messages];
    }, [] as string[]);
  };
};

export const validateArray =
  <T>(validateItem: ValidationFunc<T>): ValidationFunc<T[]> =>
  (list: T[]) =>
    list.reduce((acc, item) => [...acc, ...validateItem(item)], [] as string[]);

export const validateSome =
  <T>(validateItem: ValidationFunc<T>): ValidationFunc<T[]> =>
  (list: T[]) => {
    const invalid = list.filter((item) => validateItem(item).length !== 0);
    return invalid.length !== list.length || list.length === 0
      ? []
      : validateItem(invalid[0]);
  };

export const isValid =
  <T>(f: ValidationFunc<T>) =>
  (t: T): boolean =>
    !!!f(t).length;

export const isInvalid =
  <T>(f: ValidationFunc<T>) =>
  (t: T): boolean =>
    !!f(t).length;

export const validateRange =
  (min: number, max: number, errorMessage: string): ValidationFunc<number> =>
  (n: number) =>
    min <= n && max >= n ? [] : [errorMessage];

export const validateMultipleOf =
  (mul: number, errorMessage: string): ValidationFunc<number> =>
  (n: number) =>
    n % mul === 0 ? [] : [errorMessage];

export const stringEmpty =
  (validationMessage: string) =>
  (field: string): string[] =>
    field.trim() !== "" ? [] : [validationMessage];

export const maximumCharacterLimit =
  (stringLength: number, validationMessage: string) =>
  (field: string): string[] =>
    field.length > stringLength ? [validationMessage] : [];

export const minimumCharacterLimit =
  (stringLength: number, validationMessage: string) =>
  (field: string): string[] =>
    field.length < stringLength ? [validationMessage] : [];

export const notEmpty =
  (validationMessage: string = FIELD_REQUIRED_MSG) =>
  (field: Nullable<string>): string[] =>
    (field || "").trim() !== "" ? [] : [validationMessage];

export const allowEmpty =
  (validationFunc: ValidationFunc<string>): ValidationFunc<string> =>
  (field: string): string[] =>
    field === "" ? [] : validationFunc(field);

export const allowNull = <T>(
  validationFunc: ValidationFunc<T>
): ValidationFunc<Nullable<T>> => {
  return (t: Nullable<T>) =>
    t === null || t === undefined ? [] : validationFunc(t);
};

export const notNull =
  <T>(message: string = FIELD_REQUIRED_MSG) =>
  (model: Nullable<T>) =>
    model === null ? [message] : [];

export const isNull =
  <T>(message: string = FIELD_REQUIRED_MSG) =>
  (model: Nullable<T>) =>
    model === null ? [] : [message];
